// Settings → Rundown: which Home Roles the Availability report treats as
// non-delivery. Non-delivery rows are hidden by "Delivery roles only" and left
// out of the report's tiles and group totals. Home Role is the NOS job title.
import { useEffect, useMemo, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useNonDeliveryHomeRoles, useUpdateNonDeliveryHomeRoles } from "@/hooks/use-rundown-config";

export function NonDeliveryHomeRolesSection() {
  const { data, isLoading, isError, error, refetch } = useNonDeliveryHomeRoles();
  const save = useUpdateNonDeliveryHomeRoles();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  // Seed from the server, and re-seed after a save so the boxes show what was stored.
  useEffect(() => {
    if (data) setSelected(new Set(data.nonDelivery));
  }, [data]);

  // A role already marked non-delivery stays listed even if no synced person holds it now.
  const options = useMemo(() => {
    if (!data) return [];
    return [...new Set([...data.knownHomeRoles, ...data.nonDelivery])].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [data]);

  const toggle = (homeRole: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(homeRole);
      else next.delete(homeRole);
      return next;
    });
  };

  return (
    <section className="space-y-4">
      <h3 className="text-base font-semibold">Non-delivery Home Roles</h3>
      <p className="max-w-3xl text-sm text-muted-foreground">
        People in these Home Roles are not staffed onto Solutions. The Availability report hides
        them under &ldquo;Delivery roles only&rdquo; and leaves them out of its tiles and group
        totals.
      </p>

      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load Home Roles"}
          onRetry={() => refetch()}
        />
      ) : isLoading || !data ? (
        <Skeleton className="h-32 w-full" />
      ) : options.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No Home Roles have been synced from NOS yet, so there is nothing to choose from.
        </p>
      ) : (
        <>
          <fieldset className="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
            <legend className="sr-only">Non-delivery Home Roles</legend>
            {options.map((homeRole) => {
              const id = `non-delivery-${homeRole}`;
              return (
                <div key={homeRole} className="flex items-center gap-2">
                  <Checkbox
                    id={id}
                    checked={selected.has(homeRole)}
                    onCheckedChange={(checked) => toggle(homeRole, checked === true)}
                  />
                  <Label htmlFor={id} className="text-sm font-normal">
                    {homeRole}
                  </Label>
                </div>
              );
            })}
          </fieldset>
          <Button
            variant="outline"
            onClick={() => save.mutate([...selected].sort((a, b) => a.localeCompare(b)))}
            disabled={save.isPending}
          >
            {save.isPending ? "Saving…" : "Save non-delivery Home Roles"}
          </Button>
        </>
      )}
    </section>
  );
}
